import { useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import { candidateApi } from "./api";
import { initPixel } from "./pixel";

const SOURCE_BY_QUERY: Record<string, string> = {
  telegram: "telegram",
  discord: "discord",
  facebook: "facebook",
  referral: "referral",
};

/**
 * Le bouton « Postuler » d'une landing de recrutement : la source de la pub,
 * le pixel Meta, et la candidature qui s'ouvre. Partagé par `/candidature` et
 * par la landing Cyrano (`/rejoindre`) — deux pages, UN tunnel.
 *
 * `campagneParDefaut` : ce que `?camp=` vaut quand l'URL n'en porte pas —
 * c'est ce qui distingue, dans Acquisition, un candidat venu par `/rejoindre`
 * d'un candidat venu par `/candidature`.
 */
export function usePostuler(campagneParDefaut?: string) {
  const source = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("src")?.toLowerCase();
    return (value && SOURCE_BY_QUERY[value]) || "direct";
  }, []);

  // ⚠️ `src` ne connaît que quatre valeurs (cf. `SOURCE_BY_QUERY`) : tout le
  // reste retombe sur "direct". Une campagne pub qui voudrait s'y nommer
  // disparaîtrait donc dans le tas — et c'est le SEUL moyen de savoir laquelle
  // a ramené le candidat, le pixel Meta n'étant rattaché à aucun compte
  // publicitaire. D'où ce second paramètre, libre celui-là : `?src=facebook&camp=closer`.
  // Borné à 40 caractères et à [a-z0-9_-] pour qu'une URL bricolée n'écrive
  // rien d'autre en base.
  const sourceDetail = useMemo(() => {
    const raw = new URLSearchParams(window.location.search).get("camp")?.toLowerCase();
    const clean = (raw || "").replace(/[^a-z0-9_-]/g, "").slice(0, 40);
    return clean || campagneParDefaut;
  }, [campagneParDefaut]);

  useEffect(() => {
    initPixel();
  }, []);

  // Plus de `Lead` ici depuis le 07-10-2026 : il part à l'envoi du
  // formulaire (cf. `profileMutation` dans `RegieTestCandidatePage`).
  return useMutation({
    mutationFn: () => candidateApi.startApplication({ source, source_detail: sourceDetail }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}
